import { Check } from '@mui/icons-material';
import { CircularProgress, ListItem, styled, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';

import CircularProgressWithLabel from '../../../components/CircularProgressWithLabel';
import { ProjectRunAnalysisQuestion } from '../../../Types/dealGPT';
import { ScopeAreasStatus } from '../../../Types/enums';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useNavigationIndex } from '../../Providers/NavigationIndexProvider';
import UnprocessedChangesDot from './UnprocessedChangesDot';

type NavListItemProps = {
  current: boolean;
  previouslySeen: boolean;
  canStep: boolean;
};

const navListItemTriangleSize = 20;

const NavListItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'current' && prop !== 'previouslySeen',
})<NavListItemProps>(({ current, previouslySeen, canStep }) => ({
  alignItems: 'center',
  backgroundColor: previouslySeen ? (current ? '#CED7E6' : '#50658E') : '#E8EEF8',
  color: previouslySeen && !current ? '#ffffff' : '#757575',
  cursor: canStep ? 'pointer' : 'default',
  display: 'flex',
  flex: '1 1 auto',
  justifyContent: 'center',
  textAlign: 'center',
  padding: '12px',
  paddingRight: '16px',

  '&:first-of-type': {
    borderRadius: '100px 0px 0px 100px',

    '&::after': {
      left: window.matchMedia('(min-width: 1200px)').matches ? '50%' : '75%',
      borderRightWidth: window.matchMedia('(min-width: 1200px)').matches
        ? `${navListItemTriangleSize + 4}px`
        : `${navListItemTriangleSize - 4}px`,
    },
  },

  '&:last-of-type': {
    borderRadius: '0px 100px 100px 0px',

    '&::after': {
      left: window.matchMedia('(min-width: 1200px)').matches ? '50%' : '25%',
      borderLeftWidth: window.matchMedia('(min-width: 1200px)').matches
        ? `${navListItemTriangleSize + 4}px`
        : `${navListItemTriangleSize - 4}px`,
    },
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: `-${navListItemTriangleSize - 3}px`,
    left: '50%',
    transform: 'translateX(-50%)',
    borderLeft: `${navListItemTriangleSize + 4}px solid transparent`,
    borderRight: `${navListItemTriangleSize + 4}px solid transparent`,
    borderTop: `${navListItemTriangleSize - 2}px solid ${current ? '#CED7E6' : 'transparent'}`,
    borderBottom: 'none',
  },
}));

type AnalysisNavItemProps = {
  projectId: string;
  runId?: string;
  index: number;
  pageName: string;
  isLoadingThis: boolean;
  isLoadingApplyChanges: boolean;
  stepIndex: number;
  scope: string;
  scopeStatus: ScopeAreasStatus;
  pageQuestions: ProjectRunAnalysisQuestion[];
  isScanning: boolean;
  isScanCompleted: boolean;
  progress: number;
  isFullScan: boolean;
};

const AnalysisNavItem: React.FC<AnalysisNavItemProps> = ({
  projectId,
  runId,
  index,
  pageName,
  isLoadingThis,
  isLoadingApplyChanges,
  stepIndex,
  scope,
  scopeStatus,
  pageQuestions,
  isScanning,
  isScanCompleted,
  progress,
  isFullScan,
}) => {
  const [hasUnprocessedDownstreamChanges, setHasUnprocessedDownstreamChanges] =
    useState<boolean>(false);
  const analytics = useAnalytics();
  const { goToPage } = useNavigationIndex();

  const handleNavClick = (index: number) => {
    analytics.event(
      AnalyticsEvent.WM_IA_A_TAB_STEP,
      {
        value: pageName,
      },
      {
        projectId,
        runId,
        scope,
      }
    );
    goToPage(index);
  };

  const visited = useMemo(() => index === stepIndex, [index, stepIndex]);

  useEffect(() => {
    let maxDisplayOrder = 0;
    setHasUnprocessedDownstreamChanges(false);

    for (const question of pageQuestions) {
      if (question.displayOrder && question.displayOrder > maxDisplayOrder) {
        maxDisplayOrder = question.displayOrder;
      }
    }

    for (let i = 0; i <= maxDisplayOrder; i++) {
      const displayOrderQuestions = pageQuestions.filter((question) => question.displayOrder === i);

      if (
        displayOrderQuestions.length > 0 &&
        displayOrderQuestions[0].unprocessedDownstreamChanges === true
      ) {
        setHasUnprocessedDownstreamChanges(true);
        // If one question on the page has unprocessed downstream changes, no need to keep searching
        return;
      }
    }
  }, [pageQuestions]);

  return (
    <NavListItem
      current={index === stepIndex}
      previouslySeen={visited}
      canStep={scopeStatus === ScopeAreasStatus.COMPLETE}
      key={`${pageName}.nav`}
      onClick={() => handleNavClick(index)}
    >
      {hasUnprocessedDownstreamChanges && (
        <UnprocessedChangesDot position={{ top: '16px', right: '5px' }} />
      )}
      {!isLoadingThis &&
        !isLoadingApplyChanges &&
        visited &&
        index !== stepIndex &&
        !isScanning &&
        isScanCompleted && <Check sx={{ fontSize: '20px', marginRight: '5px' }} />}

      {isLoadingThis && isLoadingApplyChanges && progress === 100 && isScanning && (
        <Check sx={{ fontSize: '20px', marginRight: '5px' }} />
      )}

      {((index === stepIndex && isLoadingThis) || (index > stepIndex && isLoadingApplyChanges)) &&
      isFullScan &&
      progress < 100 ? (
        <CircularProgressWithLabel
          variant="determinate"
          value={progress}
          textColor={visited && index !== stepIndex ? '#ffffff' : 'inherit'}
          circleColor={visited && index !== stepIndex ? '#bbc9d9' : '#dcdcdc'}
        />
      ) : null}

      {index === stepIndex && isLoadingThis && !isFullScan ? (
        <CircularProgress size="30px" />
      ) : null}

      <Typography
        sx={{
          fontSize: '14px',
          fontWeight: '300',
          flex: 1,
          color: visited && index !== stepIndex ? '#ffffff' : 'inherit',
        }}
      >
        {pageName}
      </Typography>
    </NavListItem>
  );
};

export default AnalysisNavItem;
